import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, ContactDetail, api, parseTags } from "../api";
import TagChips from "../components/TagChips";

const stateColor: Record<string, string> = {
  queued: "bg-ink-100 text-ink-700",
  sending: "bg-amber-100 text-amber-800",
  sent: "bg-blue-100 text-blue-800",
  replied: "bg-emerald-100 text-emerald-800",
  won: "bg-green-200 text-green-900",
  lost: "bg-ink-100 text-ink-500",
  bounced: "bg-red-100 text-red-800",
  unsub: "bg-ink-200 text-ink-700",
};

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Edit state
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api<ContactDetail>(`/api/contacts/${id}`)
      .then((c) => {
        setContact(c);
        setTags(parseTags(c.tags));
        setNotes(c.notes ?? "");
        setName(c.name ?? "");
        setCompany(c.company ?? "");
        setCustomSubject(c.custom_subject ?? "");
        setCustomBody(c.custom_body ?? "");
      })
      .catch((e) => setErr(e.message));
    api<{ all_tags: string[] }>("/api/contacts?limit=1").then((r) => setAllTags(r.all_tags ?? []));
  }, [id]);

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api(`/api/contacts/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          tags, notes, name, company,
          custom_subject: customSubject,
          custom_body: customBody,
        }),
      });
      setDirty(false);
    } catch (e) {
      alert(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (err) return <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>;
  if (!contact) return <div className="text-sm text-ink-500">loading…</div>;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/contacts" className="text-xs text-ink-500 hover:text-ink-700">← All contacts</Link>
        <div className="flex items-start justify-between mt-2">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight font-mono">{contact.email}</h1>
            <div className="text-xs text-ink-500 mt-1">{contact.id}</div>
          </div>
          {dirty && (
            <button onClick={save} disabled={saving} className="btn-primary">
              {saving ? "saving…" : "Save changes"}
            </button>
          )}
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="label">Name</div>
            <input
              className="input"
              value={name}
              onChange={(e) => { setName(e.target.value); setDirty(true); }}
              placeholder="Recipient name"
            />
          </div>
          <div>
            <div className="label">Company</div>
            <input
              className="input"
              value={company}
              onChange={(e) => { setCompany(e.target.value); setDirty(true); }}
              placeholder="Business name"
            />
          </div>
        </div>

        <div>
          <div className="label">Tags</div>
          <TagChips
            value={tags}
            suggestions={allTags}
            onChange={(next) => { setTags(next); setDirty(true); }}
          />
          <p className="text-xs text-ink-400 mt-2">
            Press Enter or "," to add. Type to autocomplete from existing tags. Use tags to mark whales,
            warm leads, do-not-contact, friends-of-Austin, etc.
          </p>
        </div>

        <div>
          <div className="label">Notes (private to you)</div>
          <textarea
            className="input min-h-[120px]"
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
            placeholder="Anything you want to remember about this contact: who they are, why you're emailing them, what was said off-email, etc."
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="label">Custom email override</div>
            {(customSubject || customBody) && (
              <button
                className="text-xs text-ink-500 hover:text-red-700"
                onClick={() => { setCustomSubject(""); setCustomBody(""); setDirty(true); }}
              >
                Clear override
              </button>
            )}
          </div>
          <p className="text-xs text-ink-500 mb-2">
            When non-empty, used for the <strong>first send</strong> instead of the campaign's subject/body
            template. Reply drafts are unaffected.
          </p>
          <input
            className="input font-mono text-xs"
            placeholder="Subject (leave blank to fall back to campaign template)"
            value={customSubject}
            onChange={(e) => { setCustomSubject(e.target.value); setDirty(true); }}
          />
          <textarea
            className="input font-mono text-xs mt-2 min-h-[200px]"
            placeholder="Body (leave blank to fall back to campaign template)"
            value={customBody}
            onChange={(e) => { setCustomBody(e.target.value); setDirty(true); }}
          />
        </div>

        <details className="text-sm">
          <summary className="cursor-pointer text-xs uppercase tracking-wide text-ink-500">
            Scraped fields (read-only)
          </summary>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
            <KV k="Category" v={contact.category ?? "—"} />
            <KV k="Phone" v={contact.phone ?? "—"} mono />
            <KV k="Website" v={contact.website ?? "—"} mono />
            <KV k="Instagram" v={contact.instagram_handle ?? "—"} mono />
            <KV k="Address" v={contact.address ?? "—"} />
          </dl>
          {contact.intro && (
            <div className="mt-3 rounded-md border border-ink-200 bg-ink-50 p-3 text-sm text-ink-700">
              <div className="label">Intro / About</div>
              <p className="whitespace-pre-wrap">{contact.intro}</p>
            </div>
          )}
        </details>
      </div>

      <div className="card p-5">
        <h2 className="text-sm font-semibold mb-3">Campaign enrollments ({contact.enrollments.length})</h2>
        {contact.enrollments.length === 0 ? (
          <div className="text-sm text-ink-500">Not enrolled in any campaigns.</div>
        ) : (
          <ul className="divide-y divide-ink-100">
            {contact.enrollments.map((e) => (
              <li key={e.id} className="flex items-center justify-between py-2 text-sm gap-3">
                <Link to={`/campaigns/${e.campaign_id}`} className="hover:underline truncate">
                  {e.campaign_name}
                </Link>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span className={`pill ${stateColor[e.state] ?? "bg-ink-100"}`}>{e.state}</span>
                  {e.thread_id && (
                    <Link to={`/threads/${e.thread_id}`} className="btn-secondary text-xs px-2 py-1">
                      Thread
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-ink-100 py-1">
      <dt className="text-ink-500">{k}</dt>
      <dd className={`text-right truncate ${mono ? "font-mono text-xs" : ""}`}>{v}</dd>
    </div>
  );
}
